import { useLocale } from 'use-intl';
import { useTranslations } from '@/i18n/translations';
import { Body, Button, Stack } from '@leanin/ui';

import { RouterLinkAs } from '@/lib/router-link';
import {
  LISTING_ACTION_KEY,
  LISTING_HINT_KEY,
  causesToldState,
  listingFixes,
  type CircleListingHealth,
  type ListingFix,
} from './listing-health';

/** The quiet line at the top of a Circle's edit page once its leader has
 *  closed the listing checklist for the last time (`display: 'hint'`): the
 *  failing checks that cause the told state in one sentence, any further one
 *  in another, and the first one's fix (requests first), without the card. */
export function CircleListingHealthHint({
  circleId,
  health,
}: {
  readonly circleId: string;
  readonly health: CircleListingHealth;
}) {
  const t = useTranslations('Circles');
  const locale = useLocale();
  const fixes = listingFixes(circleId, health.checks);
  const [first] = fixes;
  // The server sends listing health only while a check fails.
  if (first === undefined) return null;
  const reasons = (of: readonly ListingFix[]) =>
    new Intl.ListFormat(locale, { type: 'conjunction' }).format(
      of.map((fix) => t('listingHealth.reason', { reason: fix.reason })),
    );
  const causes = fixes.filter((fix) => causesToldState(health.toldState, fix.reason));
  const further = fixes.filter((fix) => !causesToldState(health.toldState, fix.reason));
  return (
    <Stack gap="sm" align="flex-start">
      <Body color="mute">
        {t(LISTING_HINT_KEY[health.toldState], { reason: reasons(causes) })}
        {further.length > 0 ? ` ${t('listingHealth.hintAlso', { reason: reasons(further) })}` : null}
      </Body>
      <Button variant="secondary" size="sm" href={first.href} linkAs={RouterLinkAs}>
        {t(LISTING_ACTION_KEY[first.reason])}
      </Button>
    </Stack>
  );
}
